/**
 * Calibration Helper Utilities
 * 
 * Pure helper functions for calibration actions.
 * These functions handle specific aspects of calibration logic.
 */

import type { ICalibrationAvailability } from '../types/calibration';
import { getDynamicVisualizationMetrics } from './dynamicMetricMapping';
import { getUSOnlyRegionFilterValue } from '@intelligence/v3/utils/region';

/**
 * Update visualization metric state
 * Sets the visual metrics for charts based on calibration data
 */
export function updateVisualizationMetrics(
  metric: string,
  calibrationFlags: ICalibrationAvailability | null,
  setters: {
    setKpiVisualMetricValue: (v: string) => void;
    setVisualMetricValue: (v: string) => void;
    setVisualMetricValues: (v: string[]) => void;
  },
  jumpVisualMetricsRef: React.MutableRefObject<string[] | null>
) {
  const defaultVisualMetrics = getDynamicVisualizationMetrics(metric, calibrationFlags);

  setters.setKpiVisualMetricValue(defaultVisualMetrics[0]);
  setters.setVisualMetricValue(defaultVisualMetrics[0]);
  setters.setVisualMetricValues(defaultVisualMetrics);
  jumpVisualMetricsRef.current = defaultVisualMetrics;

  console.log('[updateVisualizationMetrics] Updated visualization metrics:', defaultVisualMetrics);
}

/**
 * Build URL parameters for calibration view
 */
export function buildCalibrationUrlParams(
  metric: string,
  filterParams: any,
  visualMetric: string,
  byDevice: boolean
): any {
  return {
    ...filterParams,
    segment: metric,
    visualMetricValue: visualMetric,
    dateType: 'day',
    byDevice,
  };
}

/**
 * Sync calibration view state
 * Coordinates filter updates, URL updates, and data fetching
 */
export function syncCalibrationViewState(
  filterParams: any,
  urlParams: any,
  actions: {
    setFilterValue: (v: any) => void;
    updateUrl?: (params: any, config: any, flag: boolean) => void;
    updateFilterDatas: (...args: any[]) => void;
  },
  refs: {
    userManuallyChangedRegionRef: React.MutableRefObject<boolean>;
    userSegmentChangedRegionRef: React.MutableRefObject<boolean>;
    userSegmentCloseRef: React.MutableRefObject<boolean>;
    restoreDefault: React.MutableRefObject<boolean>;
    specialCascader: React.MutableRefObject<boolean>;
  },
  urlConfig: any
) {
  // Update filter state
  actions.setFilterValue(filterParams);

  // Update URL
  if (actions.updateUrl) {
    actions.updateUrl(urlParams, urlConfig, true);
  }

  // Trigger data fetch
  actions.updateFilterDatas(
    filterParams,
    false,
    false,
    true,
    refs.userManuallyChangedRegionRef.current,
    {},
    refs.userSegmentChangedRegionRef.current,
    refs.userSegmentCloseRef.current,
    refs.restoreDefault.current,
    refs.specialCascader.current
  );
}

/**
 * Get US-only region filter value
 * Used for M Science data source which requires US region
 */
export function getUSOnlyRegionFilter(filterValue: any): any {
  return getUSOnlyRegionFilterValue(filterValue);
}
